import * as React from "react";
import { AnyTaggedItem } from "src/types";
import { TaskForm } from ".";
import { Habit, Task } from "src/database/types";
import { createItem, updateItem } from "src/database/queries/items";

interface EditModal {
  mode: "edit";
  item: AnyTaggedItem;
}

interface CreateModal {
  mode: "create";
  item: Habit | Task;
}

type Modal = EditModal | CreateModal;

export const taskFormContext = React.createContext<TaskFormContext>({} as any);

export type TaskFormContext = ReturnType<typeof createTaskFormContext>;

export function createTaskFormContext() {
  const [modal, setModal] = React.useState<Modal | null>(null);
  const create = React.useCallback(
    (item: Task | Habit) => {
      setModal({ mode: "create", item });
    },
    [setModal],
  );

  const edit = React.useCallback(
    (item: AnyTaggedItem) => {
      setModal({ mode: "edit", item });
    },
    [setModal],
  );

  const close = React.useCallback(() => {
    setModal(null);
  }, [setModal]);

  const update = React.useCallback(
    (item: AnyTaggedItem) => {
      if (!modal) return;
      switch (modal.mode) {
        case "create":
          if (item.type === "instance") return;
          return setModal({ ...modal, item });
        case "edit":
          return setModal({ ...modal, item });
      }
    },
    [modal, setModal],
  );

  const submit = React.useCallback(() => {
    if (modal) {
      switch (modal.mode) {
        case "create":
          createItem(modal.item);
          break;
        case "edit":
          updateItem(modal.item);
          break;
      }
    }
    close();
  }, [modal, close]);

  return {
    item: modal?.item ?? null,
    create,
    edit,
    close,
    update,
    submit,
  } as const;
}

export function TaskFormProvider({
  children,
  value,
}: React.PropsWithChildren<{ value: TaskFormContext }>) {
  return (
    <taskFormContext.Provider value={value}>
      <TaskForm form={value} />
      {children}
    </taskFormContext.Provider>
  );
}
